import { db } from '../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query, 
  where,
  serverTimestamp
} from 'firebase/firestore';
import { classService } from './classService';

const CLASS_EVENTS_COLLECTION = 'classEvents';

export const classEventService = {
  // Create a class event and automatically add it to all class members
  async createClassEvent(eventData, classID) {
    try {
      // First, add the event to the class events collection
      const classEventRef = await addDoc(collection(db, CLASS_EVENTS_COLLECTION), {
        ...eventData,
        classID: classID,
        createdAt: serverTimestamp(),
        isClassEvent: true
      });

      // Get all class members
      const classMembers = await classService.getClassMembers(classID);
      
      // Add this event to each class member's accepted events
      const memberEventPromises = classMembers.map(async (member) => {
        const memberEventData = {
          ...eventData,
          classEventId: classEventRef.id,
          classID: classID,
          isClassEvent: true,
          userId: member.id,
          createdAt: serverTimestamp(),
          // Override participants to be empty for class events since they're visible to all class members
          participants: [],
          participant: ''
        };

        return addDoc(collection(db, 'acceptedEvents'), memberEventData);
      });

      await Promise.all(memberEventPromises);

      console.log(`✅ Class event created and added to ${classMembers.length} class members`);
      
      return {
        classEventId: classEventRef.id,
        memberCount: classMembers.length
      };
    } catch (error) {
      console.error('Error creating class event:', error);
      throw error;
    }
  },

  // Get all class events for a specific class
  async getClassEvents(classID) {
    try {
      const q = query(
        collection(db, CLASS_EVENTS_COLLECTION),
        where('classID', '==', classID)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching class events:', error);
      throw error;
    }
  },

  // Get all existing class events that should be distributed to a new member
  async getExistingClassEventsForNewMember(classID) {
    try {
      // Get all class events from the master collection
      const classEvents = await this.getClassEvents(classID);
      
      // Filter to only include events that haven't passed yet (optional - you might want all events)
      // For now, let's include all events so new members get the full history
      
      console.log(`Found ${classEvents.length} existing class events for new member`);
      return classEvents;
    } catch (error) {
      console.error('Error fetching existing class events for new member:', error);
      throw error;
    }
  },

  // Distribute existing class events to a new class member
  async distributeExistingEventsToNewMember(newMemberUserId, classID) {
    try {
      // Get all existing class events
      const existingClassEvents = await this.getExistingClassEventsForNewMember(classID);
      
      if (existingClassEvents.length === 0) {
        console.log('No existing class events to distribute');
        return { addedCount: 0, totalEvents: 0 };
      }

      // Add each class event to the new member's accepted events
      const addEventPromises = existingClassEvents.map(async (classEvent) => {
        // Check if member already has this event to avoid duplicates
        const existingEventQuery = query(
          collection(db, 'acceptedEvents'),
          where('userId', '==', newMemberUserId),
          where('classEventId', '==', classEvent.id)
        );
        
        const existingSnapshot = await getDocs(existingEventQuery);
        
        if (existingSnapshot.empty) {
          // Create the event for this new member
          const memberEventData = {
            // Copy all the original event data
            ...classEvent,
            // Override specific fields for the new member
            classEventId: classEvent.id,
            userId: newMemberUserId,
            createdAt: serverTimestamp(),
            isClassEvent: true,
            // Clear participant fields since class events are visible to all members
            participants: [],
            participant: '',
            // Mark as historical distribution
            distributedAt: serverTimestamp(),
            isHistoricalClassEvent: true
          };

          return addDoc(collection(db, 'acceptedEvents'), memberEventData);
        } else {
          console.log(`Member already has class event: ${classEvent.eventName}`);
          return null;
        }
      });

      // Wait for all events to be added
      const results = await Promise.all(addEventPromises);
      const addedCount = results.filter(result => result !== null).length;

      console.log(`✅ Distributed ${addedCount} existing class events to new member`);
      
      return {
        addedCount,
        totalEvents: existingClassEvents.length
      };
    } catch (error) {
      console.error('Error distributing existing events to new member:', error);
      throw error;
    }
  },

  // Convert an existing event to a class event and distribute to all class members
  async convertToClassEvent(eventData, classID, originalEventId) {
    try {
      // First, add the event to the class events collection
      const classEventRef = await addDoc(collection(db, CLASS_EVENTS_COLLECTION), {
        ...eventData,
        classID: classID,
        createdAt: serverTimestamp(),
        isClassEvent: true,
        originalEventId: originalEventId // Track which event this was converted from
      });

      // Get all class members
      const classMembers = await classService.getClassMembers(classID);
      
      // Add this event to each class member's accepted events (except the admin who already has it)
      const memberEventPromises = classMembers
        .filter(member => member.id !== eventData.userId) // Skip the admin who already has the event
        .map(async (member) => {
          // Check if this member already has a similar event to avoid duplicates
          const existingEventsQuery = query(
            collection(db, 'acceptedEvents'),
            where('userId', '==', member.id),
            where('eventName', '==', eventData.eventName),
            where('day', '==', eventData.day),
            where('month', '==', eventData.month),
            where('year', '==', eventData.year)
          );
          
          const existingSnapshot = await getDocs(existingEventsQuery);
          
          // If no duplicate exists, add the event
          if (existingSnapshot.empty) {
            const memberEventData = {
              ...eventData,
              classEventId: classEventRef.id,
              classID: classID,
              isClassEvent: true,
              userId: member.id,
              createdAt: serverTimestamp(),
              // Override participants to be empty for class events since they're visible to all class members
              participants: [],
              participant: '',
              // Keep original event reference
              convertedFromEventId: originalEventId
            };

            return addDoc(collection(db, 'acceptedEvents'), memberEventData);
          } else {
            console.log(`Skipping duplicate event for member ${member.email}`);
            return null;
          }
        });

      // Wait for all member events to be added
      const results = await Promise.all(memberEventPromises);
      const addedCount = results.filter(result => result !== null).length;

      console.log(`✅ Event converted to class event and added to ${addedCount} class members`);
      
      return {
        classEventId: classEventRef.id,
        memberCount: addedCount,
        totalMembers: classMembers.length
      };
    } catch (error) {
      console.error('Error converting event to class event:', error);
      throw error;
    }
  },

  // Sync admin edits to all class members when a class event is updated
  async syncClassEventToAllMembers(updatedEventData, classEventId, adminUserId) {
    try {
      console.log(`🔄 Syncing class event ${classEventId} to all members...`);
      console.log('📋 Updated event data:', updatedEventData);
      console.log('🔑 Class event ID:', classEventId);

      // First, update the master class event record
      const classEventRef = doc(db, CLASS_EVENTS_COLLECTION, classEventId);
      await updateDoc(classEventRef, {
        ...updatedEventData,
        lastModified: serverTimestamp(),
        lastModifiedBy: adminUserId
      });

      // Get all class members for this class
      const classMembers = await classService.getClassMembers(updatedEventData.classID);
      
      // Update all member copies of this class event
      const updatePromises = classMembers.map(async (member) => {
        console.log(`🔍 Looking for class event copy for member: ${member.email} (${member.id})`);
        
        // Find the member's copy of this class event
        const memberEventQuery = query(
          collection(db, 'acceptedEvents'),
          where('userId', '==', member.id),
          where('classEventId', '==', classEventId)
        );
        
        const memberEventSnapshot = await getDocs(memberEventQuery);
        console.log(`📊 Found ${memberEventSnapshot.docs.length} events for member ${member.email}`);
        
        if (!memberEventSnapshot.empty) {
          // Update each member's copy (there should only be one, but handle multiple just in case)
          const updateMemberPromises = memberEventSnapshot.docs.map(async (memberEventDoc) => {
            const currentData = memberEventDoc.data();
            console.log(`📝 Current member event data for ${member.email}:`, {
              hour: currentData.hour,
              minute: currentData.minute,
              endHour: currentData.endHour,
              endMinute: currentData.endMinute,
              timeText: currentData.timeText
            });
            
            const updateData = {
              ...updatedEventData,
              userId: member.id, // Preserve the member's userId
              classEventId: classEventId, // Preserve the class event reference
              isClassEvent: true, // Ensure it stays marked as class event
              lastModified: serverTimestamp(),
              lastModifiedBy: adminUserId,
              syncedAt: serverTimestamp(), // Track when this copy was synced
              // Reset personalization flags when admin updates
              isPersonalized: false,
              personalizedBy: null,
              personalizedAt: null,
              // Track this admin update
              lastAdminUpdate: serverTimestamp(),
              lastAdminUpdateBy: adminUserId
            };
            
            console.log(`📝 Updating member event for ${member.email} with:`, {
              hour: updateData.hour,
              minute: updateData.minute,
              endHour: updateData.endHour,
              endMinute: updateData.endMinute,
              timeText: updateData.timeText
            });
            
            await updateDoc(memberEventDoc.ref, updateData);
          });
          
          await Promise.all(updateMemberPromises);
          console.log(`✅ Updated class event for member: ${member.email}`);
        } else {
          console.log(`⚠️ Member ${member.email} doesn't have this class event - skipping`);
        }
      });

      await Promise.all(updatePromises);
      
      console.log(`✅ Class event synced to ${classMembers.length} members`);
      
      return {
        syncedToMembers: classMembers.length,
        classEventId: classEventId
      };
    } catch (error) {
      console.error('Error syncing class event to members:', error);
      throw error;
    }
  },

  // Check if a user can edit a specific class event
  async canUserEditClassEvent(userId, classEventId, classID) {
    try {
      // First check if user is part of this class
      const userClasses = await classService.getUserClasses(userId);
      const isInClass = userClasses.some(c => c.id === classID);
      
      if (!isInClass) {
        return {
          canEdit: false,
          reason: 'You are not a member of this class'
        };
      }
      
      // All class members can edit their own copy of class events
      return {
        canEdit: true,
        reason: 'Class members can personalize their copy'
      };
    } catch (error) {
      console.error('Error checking edit permissions:', error);
      return {
        canEdit: false,
        reason: 'Error checking permissions'
      };
    }
  },

  // Delete a class event from all members
  async deleteClassEventFromAllMembers(classEventId, classID, adminUserId) {
    try {
      console.log(`🗑️ Deleting class event ${classEventId} from all members...`);

      // First, delete the master class event record
      const classEventRef = doc(db, CLASS_EVENTS_COLLECTION, classEventId);
      await deleteDoc(classEventRef);
      console.log('✅ Deleted master class event record');

      // Get all class members
      const classMembers = await classService.getClassMembers(classID);
      
      // Delete from all member calendars
      const deletePromises = classMembers.map(async (member) => {
        console.log(`🔍 Looking for class event to delete for member: ${member.email}`);
        
        // Find the member's copy of this class event
        const memberEventQuery = query(
          collection(db, 'acceptedEvents'),
          where('userId', '==', member.id),
          where('classEventId', '==', classEventId)
        );
        
        const memberEventSnapshot = await getDocs(memberEventQuery);
        console.log(`📊 Found ${memberEventSnapshot.docs.length} events to delete for member ${member.email}`);
        
        if (!memberEventSnapshot.empty) {
          // Delete each member's copy
          const deleteMemberPromises = memberEventSnapshot.docs.map(async (memberEventDoc) => {
            await deleteDoc(memberEventDoc.ref);
          });
          
          await Promise.all(deleteMemberPromises);
          console.log(`✅ Deleted class event for member: ${member.email}`);
        }
      });

      await Promise.all(deletePromises);
      
      console.log(`✅ Class event deleted from ${classMembers.length} members`);
      
      return {
        deletedFromMembers: classMembers.length,
        classEventId: classEventId
      };
    } catch (error) {
      console.error('Error deleting class event from members:', error);
      throw error;
    }
  }
};